import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Mail, Smartphone, Loader2 } from 'lucide-react';
import { ADMIN_PALETTE } from './adminDesignSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: '#FFFFFF',
  border: `1px solid ${ADMIN_PALETTE.border}`,
  borderRadius: 16,
  padding: '18px 20px',
  marginBottom: 12,
  boxShadow: '0 1px 2px rgba(15,17,21,0.03)',
};

const toggleShellStyle = (isOn) => ({
  position: 'relative',
  width: 40,
  height: 22,
  borderRadius: 999,
  background: isOn ? '#111827' : '#D1D5DB',
  transition: 'background 160ms ease',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
});

const toggleKnobStyle = (isOn) => ({
  position: 'absolute',
  top: 3,
  left: isOn ? 21 : 3,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#FFFFFF',
  transition: 'left 160ms ease',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const channelRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 0',
  borderTop: `1px solid ${ADMIN_PALETTE.border}`,
};

/**
 * ChannelToggle — a single row toggle for one delivery channel on one
 * event (e.g. Push for "New venue application"). Auto-saves via the
 * parent's `onChange` handler; no explicit save button.
 */
function ChannelToggle({ label, description, icon: Icon, isOn, onChange, testId, disabled }) {
  return (
    <div style={channelRowStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: '#F3F4F6',
            color: ADMIN_PALETTE.inkSoft,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={16} strokeWidth={2.1} />
        </span>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 700, color: ADMIN_PALETTE.ink }}>
            {label}
          </p>
          {description ? (
            <p style={{ margin: '2px 0 0', fontSize: 12.5, color: ADMIN_PALETTE.inkMuted, lineHeight: 1.35 }}>
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!isOn)}
        data-testid={testId}
        aria-pressed={isOn}
        aria-label={`${label}: ${isOn ? 'on' : 'off'}`}
        disabled={disabled}
        style={{ ...toggleShellStyle(isOn), opacity: disabled ? 0.55 : 1 }}
      >
        <span style={toggleKnobStyle(isOn)} />
      </button>
    </div>
  );
}

/**
 * AdminNotificationsPanel — per-admin notification preferences.
 *
 * Each admin controls their own delivery channels (Push + Email) for
 * every awareness event independently. Settings for other admins on the
 * same panel are untouched. Backed by `/api/admin/notification-prefs`.
 */
export function AdminNotificationsPanel({ headers }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [email, setEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notification-prefs`, { headers });
      setEvents(res.data?.events || []);
      setPrefs(res.data?.prefs || {});
      setEmail(res.data?.email || '');
    } catch (err) {
      toast.error('Couldn\u2019t load your notification settings.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (nextPrefs) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/notification-prefs`,
        { prefs: nextPrefs },
        { headers },
      );
      setPrefs(res.data?.prefs || nextPrefs);
    } catch (err) {
      toast.error('Couldn\u2019t save. Try again.');
      // Reload to resync with the server state so the UI never shows a
      // toggle that didn't actually persist.
      load();
    } finally {
      setSaving(false);
    }
  }, [headers, load]);

  const toggleChannel = (eventKey, channel, next) => {
    const current = prefs[eventKey] || { push: true, email: true };
    const updated = { ...current, [channel]: !!next };
    const nextPrefs = { ...prefs, [eventKey]: updated };
    setPrefs(nextPrefs);      // optimistic
    persist(nextPrefs);
  };

  if (loading) {
    return (
      <div
        data-testid="admin-notifications-panel-loading"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: ADMIN_PALETTE.inkMuted }}
      >
        <Loader2 size={16} className="animate-spin" />
        Loading your notification settings…
      </div>
    );
  }

  return (
    <div data-testid="admin-notifications-panel" style={{ padding: '4px 4px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: '#EEF2FF',
            color: '#3730A3',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Bell size={20} strokeWidth={2.1} />
        </span>
        <div>
          <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, color: ADMIN_PALETTE.ink }}>
            Notifications
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: ADMIN_PALETTE.inkMuted }}>
            Per-admin delivery. Toggles only affect <strong>your</strong> phone + <strong>your</strong> inbox
            {email ? <> ({email})</> : null}.
          </p>
        </div>
        {saving ? (
          <span
            data-testid="admin-notifications-saving"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, color: ADMIN_PALETTE.inkMuted, fontSize: 12 }}
          >
            <Loader2 size={14} className="animate-spin" /> Saving…
          </span>
        ) : null}
      </div>

      {events.map((event) => {
        const current = prefs[event.key] || { push: true, email: true };
        const safeKey = event.key.replace(/[^a-z0-9]+/gi, '-');
        return (
          <div
            key={event.key}
            data-testid={`admin-notifications-event-${safeKey}`}
            style={cardStyle}
          >
            <div style={{ marginBottom: 6 }}>
              <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 15.5, fontWeight: 800, color: ADMIN_PALETTE.ink }}>
                {event.label}
              </p>
              {event.description ? (
                <p style={{ margin: '3px 0 0', fontSize: 13, color: ADMIN_PALETTE.inkMuted, lineHeight: 1.4 }}>
                  {event.description}
                </p>
              ) : null}
            </div>
            <ChannelToggle
              label="Push notification"
              description="Lock-screen + in-app push."
              icon={Smartphone}
              isOn={!!current.push}
              onChange={(next) => toggleChannel(event.key, 'push', next)}
              testId={`admin-notifications-toggle-${safeKey}-push`}
              disabled={saving}
            />
            <ChannelToggle
              label="Email"
              description={email ? `Sent to ${email}.` : 'Sent to your admin account email.'}
              icon={Mail}
              isOn={!!current.email}
              onChange={(next) => toggleChannel(event.key, 'email', next)}
              testId={`admin-notifications-toggle-${safeKey}-email`}
              disabled={saving}
            />
          </div>
        );
      })}
    </div>
  );
}

export default AdminNotificationsPanel;
